import { Subject, concatMap, delay, generate, of, takeUntil } from 'rxjs'
import Widget from './widget.js'

/**
 * CounterWidget module
 * @module @moreonion/socialproof/counter
 */

/**
 * A widget for counting up to a target.
 * @extends Widget
 */
export default class CounterWidget extends Widget {
  /**
   * Default settings
   * @type {object}
   */
  static defaults = {
    counter: 'current',
    targets: [100, 1000, 10000, 100000],
    threshold: 95,
    offset: 0,
    locale: undefined,
    animate: false,
    animationIncrement: 1,
    animationInterval: 100,
  }

  // Private observable for stopping a counter animation
  #stopCounter$ = new Subject()

  /**
   * Create a widget.
   * @param {HTMLElement} element - The widget’s HTML element
   * @param {object} [options] - Options for the widget
   * @param {'current'|'target'|'needed'|'progress'} [counter='current'] - The display value
   * @param {array} [options.targets=[ 100, 1000, 10000, 100000 ]] - The counter’s target(s)
   * @param {number} [options.threshold=90] - Threshhold % for switching to the next target
   * @param {number} [options.offset=0] - Number to add to the count
   * @param {string} [options.locale] - Locale for formatting (defaults to the html lang)
   * @param {boolean|string} [options.animate=false] - Animate counter updates
   * @param {number} [options.animationIncrement=1] - Increment for each step of the animation
   * @param {number} [options.animationInterval=100] - Milliseconds delay between animation steps
   * @param {string} [options.pathToValue=''] - JSON path from which to read the data
   * @param {string|null} [options.styleProp=null] - CSS property to update instead of the content
   * @param {string|null} [options.htmlAttr=null] - HTML attribute to update instead of the content
   * @param {function|null} [options.formatter=null] - Function to modify the output of a value
   * @param {number|null} [options.pollLimit=null] - Limit to the number of polls
   */
  constructor (element, options) {
    super(element, {
      ...CounterWidget.defaults,
      ...options,
    })
    // Cast values from dataset to an appropriate type.
    this.settingsToNumber('threshold', 'offset', 'animationIncrement', 'animationInterval')
    if (typeof this.settings.targets === 'string') {
      this.settings.targets = this.settings.targets.split(',').map(
        (target) => parseInt(target.replace(/\[|\]/, '').trim())
      )
    }
    if (typeof this.settings.animate === 'string') {
      // If `data-animate` is set, any value is regarded as true.
      this.settings.animate = true
    }
    // Set locale
    if (!this.settings.locale) {
      // Element.prototype.closest can’t be tested using jsdom
      this.settings.locale = element.closest(['lang'])?.lang /* c8 ignore next */
    }
    // Set default formatter
    if (!this.settings.formatter) {
      this.settings.formatter = this.defaultFormatter()
    }
    // Reduce animation increment for progress counters (raw value between 0 and 1)
    if (this.settings.counter === 'progress') {
      this.settings.animationIncrement = this.settings.animationIncrement / 100
    }
  }

  /**
   * Find an appropriate formatter function based on the settings.
   *
   * @returns {function|null} The formatter function
   */
  defaultFormatter () {
    if (!(this.settings.styleProp || this.settings.htmlAttr) || this.settings.styleProp === 'content') {
      if (this.settings.counter === 'progress') {
        return Intl.NumberFormat(this.settings.locale, { style: 'percent' }).format
      }
      return Intl.NumberFormat(this.settings.locale).format
    }
    if (this.settings.counter === 'progress' && !['value', 'aria-valuenow'].includes(this.settings.htmlAttr)) {
      return (value) => `${value * 100}%`
    }
    return null
  }

  /**
   * Get the current count based on the latest poll.
   *
   * @param {number} value - The polled number
   *
   * @returns {number} The current count including offset (0 if count < 0)
   */
  getCount (value) {
    return Math.max(value + this.settings.offset, 0)
  }

  /**
   * Get the current target based on the latest poll.
   *
   * @param {number} value - The polled number
   *
   * @returns {number} The next highest target
   */
  getTarget (value) {
    const count = this.getCount(value)
    const targets = this.settings.targets.filter(
      (target) => count * 100 / target <= this.settings.threshold
    )
    return targets.length ? Math.min(...targets) : Math.max(...this.settings.targets)
  }

  /**
   * Get the difference between the current count and target.
   *
   * @param {number} value - The polled number
   *
   * @returns {number} The needed number (0 if count > target)
   */
  getNeeded (value) {
    return Math.max(this.getTarget(value) - this.getCount(value), 0)
  }

  /**
   * Get the percentage the current count reaches of the current target.
   *
   * @param {number} value - The polled number
   *
   * @returns {number} A number between 0 and 1 representing the percentage
   */
  getProgress (value) {
    return Math.max(Math.min(this.getCount(value) / this.getTarget(value), 1), 0)
  }

  /**
   * Get the configured value.
   *
   * @param {object} data - Plain object
   *
   * @returns {number} The found value
   */
  getValue (data) {
    const value = super.getValue(data)
    if (typeof value === 'undefined') {
      return value
    }
    switch (this.settings.counter) {
      case 'current':
        return this.getCount(value)
      case 'target':
        return this.getTarget(value)
      case 'needed':
        return this.getNeeded(value)
      case 'progress':
        return this.getProgress(value)
    }
  }

  /**
   * Update the widget’s HTML.
   *
   * @param {string|number} value - The new value
   */
  async update (value) {
    return new Promise(resolve => {
      if (this.settings.animate && typeof this.value === 'number' && typeof value === 'number') {
        return this.steps$(this.value, value).subscribe({
          next: step => super.update(step),
          complete: () => {
            // Make sure update to the exact value unless the animation was cancelled
            if (this.value !== value && Math.abs(this.value - value) < this.settings.animationIncrement) {
              super.update(value)
            }
            resolve(this.value)
          }
        })
      } else {
        super.update(value)
        resolve(this.value)
      }
    })
  }

  /**
    * Transition from one value to another by emitting intermediate values.
    *
    * @param {number} start - The value to start from
    * @param {number} end - The value to count to
    *
    * @returns {rxjs.Observable} Observer counting from start to end value
    */
  steps$ (start, end) {
    let increment = this.settings.animationIncrement
    const interval = this.settings.animationInterval
    // Invert increment when counting down
    if (start > end) {
      increment = increment * -1
    }
    // Stop any ongoing animations
    this.#stopCounter$.next()
    // Create new Observable
    return generate({
      initialState: start + increment,
      iterate: step => step + increment,
      condition: step => start < end ? step <= end : step >= end,
    }).pipe(
      // Add a delay before each step
      concatMap(item => of(item).pipe(delay(interval))),
      takeUntil(this.#stopCounter$),
    )
  }
}
